import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, ChevronLeft, ChevronRight, RefreshCw,
  Search, Smartphone, Users
} from 'lucide-react';
import {
  fetchPlayerAppStatus,
  DeviceStatus,
  PlayerAppStatusRecord
} from './playerAppStatusService';

const PAGE_SIZE = 25;

const statusLabels: Record<string, string> = {
  active: 'Active device token',
  no_active_device: 'No active device token',
  no_device_record: 'No device record'
};

const statusStyles: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  no_active_device: 'bg-amber-50 text-amber-700',
  no_device_record: 'bg-slate-100 text-slate-600'
};

const formatDate = (value: string | null) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not available'
    : date.toLocaleString('en-IN');
};

const PlayerAppStatusPage: React.FC = () => {
  const [players, setPlayers] = useState<PlayerAppStatusRecord[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadPlayers = useCallback(async (signal: { cancelled: boolean }) => {
    setLoading(true);
    setError('');

    try {
      const result = await fetchPlayerAppStatus({
        page,
        page_size: PAGE_SIZE,
        search,
        device_status: deviceStatus
      });

      if (signal.cancelled) return;

      setPlayers(Array.isArray(result.results) ? result.results : []);
      setTotalCount(Number(result.total_count || 0));
    } catch (err: any) {
      if (signal.cancelled) return;

      setPlayers([]);
      setTotalCount(0);
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        'Unable to load player app status.'
      );
    } finally {
      if (!signal.cancelled) setLoading(false);
    }
  }, [page, search, deviceStatus]);

  useEffect(() => {
    const signal = { cancelled: false };
    loadPlayers(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadPlayers, refreshKey]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
    [totalCount]
  );

  const refresh = () => setRefreshKey(value => value + 1);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-[1600px] space-y-6">

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-blue-50 p-3 text-blue-600">
                <Smartphone className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">
                  Player App Status
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Monitor player device registrations and activity.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
            Device registration status is an indicator only.
            An inactive token does not confirm that an app was uninstalled.
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Users className="h-4 w-4" />
                Matching players
              </div>
              <div className="mt-2 text-3xl font-semibold text-slate-900">
                {totalCount.toLocaleString('en-IN')}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Activity className="h-4 w-4" />
                Current filter
              </div>
              <div className="mt-2 text-lg font-semibold text-slate-900">
                {deviceStatus === 'all' ? 'All device statuses' : statusLabels[deviceStatus]}
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="app-status-search" className="mb-2 block text-sm font-medium text-slate-700">
                Search players
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                <input
                  id="app-status-search"
                  type="search"
                  value={searchInput}
                  onChange={event => setSearchInput(event.target.value)}
                  placeholder="Name, mobile number or numeric player ID"
                  className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-3 text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="device-status-filter" className="mb-2 block text-sm font-medium text-slate-700">
                Device status
              </label>
              <select
                id="device-status-filter"
                value={deviceStatus}
                onChange={event => {
                  setDeviceStatus(event.target.value as DeviceStatus);
                  setPage(1);
                }}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm"
              >
                <option value="all">All statuses</option>
                <option value="active">Active device token</option>
                <option value="no_active_device">No active device token</option>
                <option value="no_device_record">No device record</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  {[
                    'Player', 'Mobile', 'App', 'Device status',
                    'Last device seen', 'Last login', 'Bet history'
                  ].map(heading => (
                    <th key={heading} className="px-4 py-4 font-semibold">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {players.map(player => (
                  <tr key={player.id} className="hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate-900">
                        {[player.first_name, player.last_name].filter(Boolean).join(' ') || 'Unnamed player'}
                      </div>
                      <div className="text-xs text-slate-500">
                        ID: {player.id}
                      </div>
                    </td>
                    <td className="px-4 py-4">{player.mobile_number || '-'}</td>
                    <td className="px-4 py-4">{player.app_name || '-'}</td>
                    <td className="px-4 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusStyles[player.device_status] || statusStyles.no_device_record}`}>
                        {statusLabels[player.device_status] || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-4">{formatDate(player.last_device_seen)}</td>
                    <td className="px-4 py-4">{formatDate(player.last_login)}</td>
                    <td className="px-4 py-4">{player.has_bet_history ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && !error && players.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">
              No matching players found.
            </div>
          )}

          {loading && (
            <div className="p-6 text-center text-sm text-slate-500">
              Loading player app status...
            </div>
          )}

          {error && (
            <div role="alert" className="m-4 rounded-xl bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-500">
              Page {page} of {totalPages} · {totalCount.toLocaleString('en-IN')} matching players
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                disabled={loading || page <= 1}
                onClick={() => setPage(value => Math.max(1, value - 1))}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>
              <button
                type="button"
                disabled={loading || page >= totalPages}
                onClick={() => setPage(value => value + 1)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PlayerAppStatusPage;
